import type { DisplayCue } from "@expert/contentful";
import type { DeviceCues, LocationCues, SoftwareCues } from "@expert/shared-types";

import { devtools, persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { shallow } from "zustand/shallow";
import { createWithEqualityFn } from "zustand/traditional";

// Inlined service functions (originally in detected-cues/services/)

const deselectCue = (devices: DisplayCue[], deviceToDeselect: string): DisplayCue[] => {
    return devices.map((d) =>
        d.name.toLowerCase() === deviceToDeselect.toLowerCase()
            ? { ...d, detected: false, isDetectedOnPreviousCall: false }
            : d,
    );
};

const handleDetectedCue = (devices: DisplayCue[], detectedDevices: (DeviceCues | SoftwareCues)[]) => {
    if (detectedDevices.length === 0) return devices;

    detectedDevices.forEach((detectedDevice) => {
        const located = devices.find((d) => d.name.toLowerCase() === detectedDevice.name.toLowerCase());
        if (located) {
            located.detected = true;
        } else {
            const { name, type } = detectedDevice;
            devices.push({ detected: true, isDetectedOnPreviousCall: false, name, type });
        }
    });

    return devices;
};

const setPreviousCues = (devices: DisplayCue[], previousDetectedDevices: string[] | undefined): DisplayCue[] => {
    if (previousDetectedDevices === undefined) return devices;

    const prevDevices = previousDetectedDevices?.map((device) => device.toLowerCase());
    return devices.map((item) => {
        if (prevDevices.includes(item.name.toLowerCase())) {
            return {
                ...item,
                isDetectedOnPreviousCall: true,
                detected: true,
            };
        }
        return item;
    });
};

// Store

export type Location = {
    state?: string;
    city?: string;
    timezone?: string;
};

type DetectedCuesStore = {
    displayCuesBySession: Record<string, DisplayCue[]>;
    locationBySession: Record<string, Location>;

    setDefaultPartnerCues: (sessionId: string, contentfulCues: DisplayCue[]) => DisplayCue[];
    processNewlyDetectedCue: (sessionId: string, newCues: (DeviceCues | SoftwareCues)[]) => void;
    processPreviouslyDetectedCues: (sessionId: string, previousCues: string[]) => void;
    deselectDetectedCue: (sessionId: string, cueToDeselect: string) => void;
    getDisplayCues: (sessionId: string) => DisplayCue[];

    processLocationCues: (sessionId: string, locationCues: LocationCues[]) => void;
    getLocation: (sessionId: string) => Location | undefined;

    resetEvents: (sessionId: string) => void;
};

export const useDetectedCuesStore = createWithEqualityFn<DetectedCuesStore>()(
    persist(
        immer(
            devtools((set, get) => ({
                displayCuesBySession: {},
                locationBySession: {},

                setDefaultPartnerCues: (sessionId, contentfulCues) => {
                    const cues = contentfulCues ?? [];
                    set((state) => {
                        state.displayCuesBySession[sessionId] = cues;
                    });
                    return cues;
                },
                processNewlyDetectedCue: (sessionId, newCues) => {
                    set((state) => {
                        const current = state.displayCuesBySession[sessionId] ?? [];
                        state.displayCuesBySession[sessionId] = handleDetectedCue(current, newCues);
                    });
                },
                processPreviouslyDetectedCues: (sessionId, previousCues) => {
                    set((state) => {
                        const current = state.getDisplayCues(sessionId) ?? [];
                        state.displayCuesBySession[sessionId] = setPreviousCues(current, previousCues);
                    });
                },
                deselectDetectedCue: (sessionId, cueToDeselect) => {
                    set((state) => {
                        const current = state.displayCuesBySession[sessionId] ?? [];
                        state.displayCuesBySession[sessionId] = deselectCue(current, cueToDeselect);
                    });
                },
                getDisplayCues: (sessionId) => {
                    return get().displayCuesBySession[sessionId] ?? [];
                },

                processLocationCues: (sessionId, locationCues) => {
                    const state = locationCues.find((cue) => cue.type === "LOCATION_STATE")?.name;
                    const city = locationCues.find((cue) => cue.type === "LOCATION_CITY")?.name;

                    if (state || city) {
                        set((draft) => {
                            const current = draft.locationBySession[sessionId] ?? {};
                            draft.locationBySession[sessionId] = {
                                ...current,
                                ...(state ? { state } : {}),
                                ...(city ? { city } : {}),
                            };
                        });
                    }
                },
                getLocation: (sessionId) => {
                    const location = get().locationBySession[sessionId];
                    if (!location) return undefined;

                    const { city, state, timezone } = location;
                    if (!(city || state || timezone)) return undefined;

                    return location;
                },

                resetEvents: (sessionId) => {
                    set((state) => {
                        delete state.displayCuesBySession[sessionId];
                        delete state.locationBySession[sessionId];
                    });
                },
            })),
        ),
        { name: "detected-cues-store", skipHydration: true },
    ),
    shallow,
);
